import api from './Client';

function noCacheParams<T extends Record<string, any>>(params?: T): T & { _nc: number } {
  return { ...(params || {} as T), _nc: Date.now() } as T & { _nc: number };
}

const noCacheHeaders = {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
};

// ----- Tipos -----

export type MoveLevel = 'iniciacion' | 'medio' | 'avanzado';
export type MoveType = 'walk' | 'run' | 'bike' | 'mixed';
export type RouteType = 'walk' | 'run' | 'bike';

export interface MoveProfile {
  user_id: number;
  nivel: MoveLevel;
  objetivos: string[];
  preferencias: MoveType[];
  prefiere_grupo: boolean;
  home_lat: number | null;
  home_lng: number | null;
  radio_busqueda_km: number;
  notificaciones_activas: boolean;
}

export interface MoveEvent {
  event_id: number;
  titulo: string;
  descripcion?: string | null;
  tipo: MoveType;
  nivel: MoveLevel;
  fecha_inicio: string;
  fecha_fin?: string | null;
  ubicacion_nombre?: string | null;
  lat?: number | null;
  lng?: number | null;
  plazas_max?: number | null;
  plazas_ocupadas?: number;
  is_premium: number;
  precio?: number | null;
  imagen?: string | null;
  inscripcion_url?: string | null;
  creado_por?: number | null;
  organizador_nombre?: string | null;
  organizador_foto?: string | null;
  route_id?: number | null;
  route_titulo?: string | null;
  route_tipo?: string | null;
  route_distancia_m?: number | null;
  route_desnivel_m?: number | null;
  route_duracion_min?: number | null;
  route_geometry?: any | null;
  route_lat?: number | null;
  route_lng?: number | null;
  is_joined?: boolean | number;
}

export interface MoveEventAttendee {
  fullName: string;
}

export interface MoveRouteSummary {
  route_id: number;
  titulo: string;
  descripcion?: string | null;
  tipo: RouteType;
  dificultad: number;
  distancia_m: number;
  desnivel_m: number;
  duracion_min: number;
  ubicacion_inicio_nombre?: string | null;
  lat_inicio?: number | null;
  lng_inicio?: number | null;
  imagen_principal?: string | null;
  is_ai_generated: number;
  rating_avg: number;
  votos_count: number;
  recommendation_score?: number;
  recommendation_reason?: string;
  recommendation_reasons?: string[];
}

export interface MoveRoutesPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface MoveRouteDetail extends MoveRouteSummary {
  geometry: any;
  hitos: any[];
}

export interface MoveActivity {
  activity_id: number;
  route_id: number | null;
  route_titulo: string | null;
  event_id: number | null;
  event_titulo: string | null;
  tipo: MoveType;
  distancia_m: number;
  duracion_min: number;
  desnivel_m: number;
  kcal_estimadas: number | null;
  fecha: string;
  source: 'manual' | 'strava' | 'event';
  notas: string | null;
  created_at: string;
  has_route_geometry?: boolean;
  strava_activity_id?: string | number | null;
}

export interface Recommendations {
  events: MoveEvent[];
  joined_events?: MoveEvent[];
  routes: MoveRouteSummary[];
  nudge: string | null;
  recommendation?: {
    title: string;
    subtitle?: string | null;
    basis: string[];
  };
}

export type RankingPeriod = 'weekly' | 'monthly' | 'all';

export interface RankingEntry {
  user_id: number;
  fullName: string;
  profile_pic?: string | null;
  total_m: number;
  sessions: number;
  distinct_routes: number;
  elevation_m: number;
  step_goal_days: number;
  completed_goals: number;
  puntos: number;
  position: number;
  isYou?: boolean;
}

export interface RankingData {
  period: RankingPeriod;
  top: RankingEntry[];
  you: RankingEntry | null;
}

// ----- Perfil -----

export async function fetchProfile(): Promise<MoveProfile | null> {
  const res = await api.get<{ profile: MoveProfile | null }>('/api/move/profile');
  return res.data.profile;
}

export async function saveProfile(profile: Partial<MoveProfile>): Promise<void> {
  await api.put('/api/move/profile', profile);
}

// ----- Eventos -----

export async function fetchEvents(filters: { tipo?: MoveType; nivel?: MoveLevel } = {}) {
  const res = await api.get<{ events: MoveEvent[] }>('/api/move/events', { params: filters });
  return res.data.events;
}

export async function fetchEvent(id: number) {
  const res = await api.get<{ event: MoveEvent; attendees: MoveEventAttendee[] }>(`/api/move/events/${id}`);
  return res.data;
}

export async function signupToEvent(id: number, payload: { accepted_responsibility?: boolean } = {}) {
  await api.post(`/api/move/events/${id}/signup`, payload);
}

export async function cancelEventSignup(id: number) {
  await api.delete(`/api/move/events/${id}/signup`);
}

export interface CreateEventPayload {
  titulo: string;
  descripcion?: string | null;
  tipo: MoveType;
  nivel: MoveLevel;
  fecha_inicio: string;
  fecha_fin?: string | null;
  ubicacion_nombre?: string | null;
  plazas_max?: number | null;
  is_premium?: boolean;
  precio?: number | null;
  route_id?: number | null;
  inscripcion_url?: string | null;
  imagen?: File | null;
}

export async function createEvent(payload: CreateEventPayload): Promise<{ event_id: number }> {
  const form = new FormData();
  form.append('titulo', payload.titulo);
  if (payload.descripcion) form.append('descripcion', payload.descripcion);
  form.append('tipo', payload.tipo);
  form.append('nivel', payload.nivel);
  form.append('fecha_inicio', payload.fecha_inicio);
  if (payload.fecha_fin) form.append('fecha_fin', payload.fecha_fin);
  if (payload.ubicacion_nombre) form.append('ubicacion_nombre', payload.ubicacion_nombre);
  if (payload.plazas_max != null) form.append('plazas_max', String(payload.plazas_max));
  form.append('is_premium', payload.is_premium ? '1' : '0');
  if (payload.precio != null) form.append('precio', String(payload.precio));
  if (payload.route_id != null) form.append('route_id', String(payload.route_id));
  if (payload.inscripcion_url) form.append('inscripcion_url', payload.inscripcion_url);
  if (payload.imagen) form.append('imagen', payload.imagen);
  const res = await api.post<{ ok: boolean; event_id: number }>('/api/move/events', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { event_id: res.data.event_id };
}

export async function updateEvent(id: number, payload: Partial<CreateEventPayload>): Promise<void> {
  const form = new FormData();
  if (payload.titulo != null) form.append('titulo', payload.titulo);
  if (payload.descripcion !== undefined) form.append('descripcion', payload.descripcion ?? '');
  if (payload.tipo != null) form.append('tipo', payload.tipo);
  if (payload.nivel != null) form.append('nivel', payload.nivel);
  if (payload.fecha_inicio != null) form.append('fecha_inicio', payload.fecha_inicio);
  if (payload.fecha_fin !== undefined) form.append('fecha_fin', payload.fecha_fin ?? '');
  if (payload.ubicacion_nombre !== undefined) form.append('ubicacion_nombre', payload.ubicacion_nombre ?? '');
  if (payload.plazas_max !== undefined) form.append('plazas_max', payload.plazas_max != null ? String(payload.plazas_max) : '');
  if (payload.is_premium !== undefined) form.append('is_premium', payload.is_premium ? '1' : '0');
  if (payload.precio !== undefined) form.append('precio', payload.precio != null ? String(payload.precio) : '');
  if (payload.route_id !== undefined) form.append('route_id', payload.route_id != null ? String(payload.route_id) : '');
  if (payload.inscripcion_url !== undefined) form.append('inscripcion_url', payload.inscripcion_url ?? '');
  if (payload.imagen) form.append('imagen', payload.imagen);
  await api.put(`/api/move/events/${id}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// ----- Rutas -----

export async function fetchRoutesPage(filters: {
  tipo?: RouteType;
  dificultad?: number;
  sort?: 'popular' | 'rating' | 'recent' | 'distance';
  q?: string;
  page?: number;
  limit?: number;
} = {}) {
  const res = await api.get<{ routes: MoveRouteSummary[]; pagination: MoveRoutesPagination }>('/api/move/routes', {
    params: filters,
  });
  return {
    routes: Array.isArray(res.data?.routes) ? res.data.routes : [],
    pagination: res.data?.pagination || {
      page: filters.page || 1,
      limit: filters.limit || 12,
      total: 0,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
    },
  };
}

export async function fetchRoutes(filters: { tipo?: RouteType; dificultad?: number; sort?: 'popular' | 'rating' | 'recent' | 'distance' } = {}) {
  const res = await fetchRoutesPage(filters);
  return res.routes;
}

export async function fetchRoute(id: number) {
  const res = await api.get<{ route: MoveRouteDetail; comments: any[]; photos: any[] }>(`/api/move/routes/${id}`);
  return res.data;
}

export async function fetchRouteCreatorPermission() {
  const res = await api.get<{ can_create_routes: boolean }>('/api/move/routes/creator-permission');
  return Boolean(res.data.can_create_routes);
}

export async function createRoute(payload: {
  titulo: string;
  descripcion?: string | null;
  tipo: RouteType;
  dificultad: number;
  distancia_m?: number | null;
  desnivel_m?: number;
  duracion_min?: number;
  geometry: { type: 'LineString'; coordinates: number[][] };
  hitos?: any[];
  ubicacion_inicio_nombre?: string | null;
  imagen_principal?: string | null;
  imagen?: File | null;
  is_published?: number;
}) {
  if (payload.imagen) {
    const form = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value == null || key === 'imagen') return;
      form.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    });
    form.append('imagen', payload.imagen);
    const res = await api.post<{ ok: boolean; route_id: number }>('/api/move/routes', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  }

  const { imagen: _unusedImagen, ...jsonPayload } = payload;
  void _unusedImagen;
  const res = await api.post<{ ok: boolean; route_id: number }>('/api/move/routes', jsonPayload);
  return res.data;
}

export async function voteRoute(id: number, estrellas: number) {
  await api.post(`/api/move/routes/${id}/vote`, { estrellas });
}

export async function commentOnRoute(id: number, texto: string) {
  await api.post(`/api/move/routes/${id}/comments`, { texto });
}

// ----- Actividad -----

export async function fetchMyActivities() {
  const res = await api.get<{ activities: MoveActivity[] }>('/api/move/activities', {
    params: noCacheParams(),
    headers: noCacheHeaders,
  });
  return Array.isArray(res.data?.activities) ? res.data.activities : [];
}

export async function createActivity(payload: {
  tipo: MoveType;
  distancia_m: number;
  duracion_min: number;
  desnivel_m?: number;
  kcal_estimadas?: number | null;
  fecha: string;
  route_id?: number | null;
  event_id?: number | null;
  notas?: string | null;
}) {
  const res = await api.post<{ ok: boolean; activity_id: number }>('/api/move/activities', payload);
  return res.data;
}

export async function publishActivityRoute(activityId: number, payload: { titulo?: string; descripcion?: string; is_published?: number } = {}) {
  const res = await api.post<{ ok: boolean; route_id: number }>(`/api/move/activities/${activityId}/publish-route`, payload);
  return res.data;
}

// ----- Recomendaciones -----

export async function fetchRecommendations() {
  const res = await api.get<Recommendations>('/api/move/recommendations');
  return res.data;
}

// ----- Ranking -----

export async function fetchRanking(period: RankingPeriod = 'weekly') {
  const res = await api.get<RankingData>('/api/move/ranking', {
    params: noCacheParams({ period }),
    headers: noCacheHeaders,
  });
  return res.data;
}

// ----- Steps -----

export interface StepsDay {
  fecha: string;
  steps: number;
  distance_m: number | null;
  source: 'manual' | 'pedometer' | 'strava' | 'derived' | null;
  completed: boolean;
}

export interface WeeklySteps {
  days: StepsDay[];
  todaySteps: number;
  todayGoalCompleted: boolean;
  weekTotal: number;
  completedDays: number;
  goalTarget: number;
  mondayStr: string;
  sundayStr: string;
}

export async function fetchWeeklySteps() {
  const res = await api.get<WeeklySteps>('/api/move/steps/weekly', {
    params: noCacheParams(),
    headers: noCacheHeaders,
  });
  return res.data;
}

export async function saveSteps(payload: {
  fecha: string;
  steps?: number;
  completed?: boolean;
  goal_target?: number;
  distance_m?: number | null;
  source?: 'manual' | 'pedometer' | 'strava' | 'derived';
}) {
  await api.post('/api/move/steps', payload);
}

// ----- Goals -----

export type GoalType = 'distance' | 'sessions' | 'steps' | 'elevation' | 'custom';
export type GoalPeriod = 'weekly' | 'monthly' | 'all' | 'custom';

export interface MoveGoal {
  goal_id: number;
  titulo: string;
  tipo: GoalType;
  target_value: number | null;
  current_value: number;
  unit: string | null;
  period: GoalPeriod;
  starts_at: string | null;
  ends_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export async function fetchGoals() {
  const res = await api.get<{ goals: MoveGoal[] }>('/api/move/goals', {
    params: noCacheParams(),
    headers: noCacheHeaders,
  });
  return Array.isArray(res.data?.goals) ? res.data.goals : [];
}

export async function createGoal(payload: {
  titulo: string;
  tipo?: GoalType;
  target_value?: number | null;
  unit?: string | null;
  period?: GoalPeriod;
  starts_at?: string | null;
  ends_at?: string | null;
}) {
  const res = await api.post<{ ok: boolean; goal_id: number }>('/api/move/goals', payload);
  return res.data;
}

export async function updateGoal(goalId: number, payload: Partial<{
  titulo: string;
  tipo: GoalType;
  target_value: number | null;
  unit: string | null;
  period: GoalPeriod;
  starts_at: string | null;
  ends_at: string | null;
}>) {
  await api.put(`/api/move/goals/${goalId}`, payload);
}

export async function deleteGoal(goalId: number) {
  await api.delete(`/api/move/goals/${goalId}`);
}
